import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth, AdminLayout } from "./AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, ChevronRight, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminNavigation = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [navItems, setNavItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [newItem, setNewItem] = useState({ label: "", url: "", order: 0, is_active: true, parent_id: null });
  const [showNew, setShowNew] = useState(false);
  const [addingChildFor, setAddingChildFor] = useState(null);

  useEffect(() => {
    fetchNavigation();
  }, []);

  const fetchNavigation = async () => {
    try {
      const [navRes, pagesRes] = await Promise.all([
        axios.get(`${API}/navigation`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pages`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setNavItems(navRes.data);
      setPages(pagesRes.data);
    } catch (error) {
      console.error("Error fetching navigation:", error);
    }
  };

  // Get parent items (no parent_id)
  const parentItems = navItems.filter(item => !item.parent_id);
  // Get child items for a parent
  const getChildItems = (parentId) => navItems.filter(item => item.parent_id === parentId);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const parentId = newItem.parent_id || addingChildFor;
      const siblings = parentId 
        ? getChildItems(parentId)
        : parentItems;
      const order = siblings.length > 0 ? Math.max(...siblings.map((n) => n.order)) + 1 : 1;
      
      await axios.post(
        `${API}/navigation`,
        { ...newItem, parent_id: parentId, order },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewItem({ label: "", url: "", order: 0, is_active: true, parent_id: null });
      setShowNew(false);
      setAddingChildFor(null);
      fetchNavigation();
      toast.success("Navigation item created");
    } catch (error) {
      toast.error("Failed to create navigation item");
    }
  };

  const handleAddChild = (parentId) => {
    const parent = navItems.find(i => i.id === parentId);
    setAddingChildFor(parentId);
    setNewItem({ label: "", url: "", order: 0, is_active: true, parent_id: parentId });
    setShowNew(true);
    // Scroll to form
    setTimeout(() => document.getElementById("nav-form")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const handleUpdate = async (item, updates) => {
    try {
      await axios.put(`${API}/navigation/${item.id}`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNavigation();
    } catch (error) {
      toast.error("Failed to update navigation item");
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Delete this navigation item and all its children?")) return;
    try {
      await axios.delete(`${API}/navigation/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNavigation();
      toast.success("Navigation item deleted");
    } catch (error) {
      toast.error("Failed to delete navigation item");
    }
  };

  const moveItem = async (items, index, direction) => {
    const newItems = [...items];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newItems.length) return;

    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];

    const updates = newItems.map((item, i) => ({ id: item.id, order: i + 1 }));

    try {
      await axios.put(
        `${API}/navigation/reorder`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNavigation();
    } catch (error) {
      toast.error("Failed to reorder navigation");
    }
  };

  // Check if a page exists for a given URL slug
  const getPageForUrl = (url) => {
    const slug = url.replace(/^\//, ""); // strip leading slash
    return pages.find(p => p.slug === slug);
  };

  const NavItemRow = ({ item, index, items, isChild = false, onAddChild }) => {
    const existingPage = getPageForUrl(item.url);
    return (
    <div
      className={`flex items-center gap-4 p-3 rounded-lg bg-its-teal/50 border border-its-teal-light/10 ${
        isChild ? "ml-8" : ""
      }`}
    >
      <div className="flex flex-col gap-1">
        <button
          onClick={() => moveItem(items, index, -1)}
          disabled={index === 0}
          className="p-1 text-its-offwhite/40 hover:text-white disabled:opacity-30"
        >
          ▲
        </button>
        <button
          onClick={() => moveItem(items, index, 1)}
          disabled={index === items.length - 1}
          className="p-1 text-its-offwhite/40 hover:text-white disabled:opacity-30"
        >
          ▼
        </button>
      </div>
      {isChild && <ChevronRight size={16} className="text-its-teal-light" />}
      <GripVertical size={18} className="text-its-offwhite/40" />
      <div className="flex-1 grid grid-cols-2 gap-4">
        <input
          type="text"
          value={item.label}
          onChange={(e) => handleUpdate(item, { label: e.target.value })}
          className="form-input py-1.5"
          placeholder="Label"
        />
        <input
          type="text"
          value={item.url}
          onChange={(e) => handleUpdate(item, { url: e.target.value })}
          className="form-input py-1.5"
          placeholder="URL"
        />
      </div>
      {/* Page status indicator */}
      {existingPage ? (
        <button
          onClick={() => navigate("/monkey/pages")}
          className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300 transition-colors"
          title="Page exists — click to edit"
        >
          <FileText size={14} />
          Page
        </button>
      ) : (
        <button
          onClick={async () => {
            const slug = item.url.replace(/^\//, "");
            try {
              await axios.post(`${API}/pages`, {
                title: item.label,
                slug,
                page_type: "standard",
                content: "",
                meta_description: "",
                meta_keywords: "",
                is_published: true,
                fields: {},
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success(`Page created for "${item.label}"`);
              navigate("/monkey/pages");
            } catch (err) {
              const detail = err.response?.data?.detail;
              if (typeof detail === "string" && detail.includes("already exists")) {
                toast.info("Page already exists");
                navigate("/monkey/pages");
              } else {
                toast.error("Failed to create page");
              }
            }
          }}
          className="flex items-center gap-1 text-xs text-its-gold/60 hover:text-its-gold transition-colors"
          title="No page yet — click to create"
        >
          <FileText size={14} />
          <span className="underline">+ Page</span>
        </button>
      )}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={item.is_active}
          onChange={(e) => handleUpdate(item, { is_active: e.target.checked })}
          className="w-4 h-4 rounded"
        />
        <span className="text-xs text-its-offwhite/60">Active</span>
      </label>
      {!isChild && onAddChild && (
        <button
          onClick={() => onAddChild(item.id)}
          data-testid={`add-sub-item-${item.id}`}
          className="p-2 text-its-offwhite/60 hover:text-its-gold transition-colors"
          title="Add sub-item"
        >
          <Plus size={18} />
        </button>
      )}
      <button
        onClick={() => handleDelete(item.id)}
        className="p-2 text-its-offwhite/60 hover:text-red-400 transition-colors"
      >
        <Trash2 size={18} />
      </button>
    </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-navigation">
        <div className="flex items-center justify-between">
          <h1 className="heading-3">Navigation</h1>
          <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="new-nav-btn">
            <Plus size={18} className="mr-2" />
            Add Item
          </button>
        </div>

        {/* New Item Form */}
        {showNew && (
          <form id="nav-form" onSubmit={handleCreate} className="glass-card space-y-4">
            {addingChildFor && (
              <div className="px-3 py-2 rounded-lg bg-its-gold/10 border border-its-gold/30 text-sm text-its-gold">
                Adding sub-item under: <strong>{navItems.find(i => i.id === addingChildFor)?.label}</strong>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Label</label>
                <input
                  type="text"
                  value={newItem.label}
                  onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                  required
                  className="form-input"
                  data-testid="nav-label-input"
                  placeholder="e.g. Our Team"
                />
              </div>
              <div>
                <label className="form-label">URL</label>
                <input
                  type="text"
                  value={newItem.url}
                  onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                  required
                  className="form-input"
                  placeholder="/about/team"
                  data-testid="nav-url-input"
                />
              </div>
              <div>
                <label className="form-label">Parent (Optional)</label>
                <select
                  value={newItem.parent_id || addingChildFor || ""}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setNewItem({ ...newItem, parent_id: val });
                    setAddingChildFor(val);
                  }}
                  className="form-input"
                >
                  <option value="">No Parent (Top Level)</option>
                  {parentItems.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="save-nav-btn">
                {addingChildFor ? "Add Sub-item" : "Add Item"}
              </button>
              <button type="button" onClick={() => { setShowNew(false); setAddingChildFor(null); setNewItem({ label: "", url: "", order: 0, is_active: true, parent_id: null }); }} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Info about submenus */}
        <div className="p-4 bg-its-teal-secondary/20 border border-its-teal-light/20 rounded-lg">
          <p className="text-sm text-its-offwhite/80">
            <strong className="text-its-gold">Level 2 Navigation:</strong> Create dropdown menus by selecting a parent item when adding new navigation items. Child items will appear in a dropdown on desktop and as an expandable section on mobile.
          </p>
        </div>

        {/* Navigation List */}
        <div className="glass-card">
          {navItems.length === 0 ? (
            <p className="text-its-offwhite/60 text-center py-8">No navigation items</p>
          ) : (
            <div className="space-y-2">
              {parentItems.map((item, index) => {
                const children = getChildItems(item.id);
                return (
                  <div key={item.id}>
                    <NavItemRow item={item} index={index} items={parentItems} onAddChild={handleAddChild} />
                    {children.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {children.map((child, childIndex) => (
                          <NavItemRow
                            key={child.id}
                            item={child}
                            index={childIndex}
                            items={children}
                            isChild={true}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNavigation;
